// File: InvoiceDocumentPartialPostLookup.js
import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import { httpGet, httpPost } from '../../HttpRequestHandling/httpRequestHandler';
import EnhancedDataGrid from '../Common/EnhancedDataGrid';
import { toast } from 'react-toastify';
import { formatNumber } from '../Common/Utilities';

export default class InvoiceDocumentPartialPostLookup extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			data: [],
			selectedRows: []
		};

		this.formatter = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC' });

		this.fetchPartialPostData = this.fetchPartialPostData.bind(this);
		this.handleClose = this.handleClose.bind(this);
		this.handleRepostAll = this.handleRepostAll.bind(this);
		this.checkbox_clickHandler = this.checkbox_clickHandler.bind(this);
	}

	parseSeqIds = (seqIds) => {
		if (!seqIds) return [];
		if (Array.isArray(seqIds)) return seqIds.map(String);
		return String(seqIds)
			.split(',')
			.map(s => s.trim())
			.filter(Boolean);
	};

	parseOrderIdsFromJson = (jsonContent) => {
		if (!jsonContent) return [];
		try {
			const parsed = typeof jsonContent === 'string' ? JSON.parse(jsonContent) : jsonContent;
			const invoice = parsed?.publishers?.[0]?.invoices?.[0] || {};
			const orders = invoice?.orders || [];
			return (orders || [])
				.map(o => o?.orderId)
				.filter(Boolean)
				.map(String);
		} catch (e) {
			return [];
		}
	};

	componentDidMount() {
		this.fetchPartialPostData();
	}

	fetchPartialPostData() {
		const invoiceId = this.props.invoiceDocumentId;
		if (!invoiceId) return;

		this.props.inProgress?.(true);

		httpGet(`/api/invoicedocument/partialpostdetails/${invoiceId}`)
			.then(response => {
				const invoiceTypes = new Set((response.data || []).map(row => {
					try {
						return (JSON.parse(row.jsonContent)?.publishers?.[0]?.invoices?.[0]?.invoiceType || '').toLowerCase();
					} catch (e) {
						return '';
					}
				}));
				const hasMixedInvoiceTypes = invoiceTypes.has('invoice') && invoiceTypes.has('credit memo');

				const enriched = (response.data || []).map((row, index) => {
					let invoice = {};
					try {
						const parsed = JSON.parse(row.jsonContent);
						invoice = parsed?.publishers?.[0]?.invoices?.[0] || {};
					} catch (e) { }

					let start = invoice.invoiceStartDate || row.startDate;
					let end = invoice.invoiceEndDate || row.endDate;
					const formatDate = (d) => {
						if (!d) return '';
						try {
							return new Date(d).toLocaleDateString('en-US');
						} catch {
							return d;
						}
					};
					if (start && start.indexOf('T') > -1) {
						start = start.split('T')[0];
					}
					if (end && end.indexOf('T') > -1) {
						end = end.split('T')[0];
					}

					let formattedMOS = (start || end) ? `${this.formatter.format(new Date(start))} - ${this.formatter.format(new Date(end))}` : '';


					return {
						id: `${row.invoiceNumber}_${index}`, 
						...row,
						orderIds: this.parseOrderIdsFromJson(row.jsonContent),
						invoiceAmount: hasMixedInvoiceTypes && (invoice.invoiceType || '').toLowerCase() === 'credit memo'
							? -Math.abs(Number(invoice.invoiceAmount || 0))
							: invoice.invoiceAmount ?? '',
						invoiceTaxAmount: hasMixedInvoiceTypes && (invoice.invoiceType || '').toLowerCase() === 'credit memo'
							? -Math.abs(Number(invoice.invoiceTaxAmount || 0))
							: invoice.invoiceTaxAmount ?? '',
						mosRange: formattedMOS
					};
				});
				this.setState({ data: enriched, selectedRows: [] });
				this.props.inProgress?.(false);
			})
			.catch(() => {
				this.props.inProgress?.(false);
			});
	}

	checkbox_clickHandler(event) {
		const rowId = event.target.value;
		const existing = [...this.state.selectedRows];
		const index = existing.findIndex(r => r.id === rowId);
		if (index >= 0) {
			existing.splice(index, 1);
		} else {
			const selected = this.state.data.find(d => d.id === rowId);
			if (selected) {
				existing.push(selected);
			}
		}
		this.setState({ selectedRows: existing });
	}

	handleRepostAll() {
		if (this.state.selectedRows.length === 0) return;

		this.props.inProgress?.(true);

		const modelDefId = this.props.modelDefId;

		// List-level repost should NOT send lines; it only sends the selected log items.
		const request = {
			logs: this.state.selectedRows.map(r => ({
				...r
			}))
		};

		httpPost(`/api/invoicedocument/repostmediaoceaninvoice/${modelDefId}`, request)
			.then(() => {
				toast.success("Repost successfuly");
				this.props.inProgress?.(false);
				this.props.onClose(true);

			})
			.catch(() => {
				toast.error("Repost failed");
				this.props.inProgress?.(false);
			});
	}
	handleEdit = () => {
		// Partial Post editor uses seqIds as the single source of truth for line mapping.
		// We still include orderIds for display/debugging, but the editor/grid must not join on them.
		const normalized = (this.state.data || []).map(r => ({
			intInvoiceNumber: r.intInvoiceNumber,
			posted: r.posted,
			seqIds: this.parseSeqIds(r.seqIds),
			orderIds: Array.isArray(r.orderIds) ? r.orderIds : this.parseOrderIdsFromJson(r.jsonContent)
		}));

		if (normalized.length === 0) return;

		this.props.onEdit?.(normalized);
		this.props.onClose(); // close dialog, editor opens from list
	};

	handleClose() {
		this.props.onClose();
	}

	getEligibleRows = () => {
		return this.state.data.filter(r => r.posted !== 1);
	};

	isAllSelected = () => {
		const eligible = this.getEligibleRows();
		return (
			eligible.length > 0 &&
			eligible.every(er =>
				this.state.selectedRows.some(sr => sr.id === er.id)
			)
		);
	};

	handleSelectAll = (event) => {
		const checked = event.target.checked;

		if (checked) {
			this.setState({
				selectedRows: this.getEligibleRows()
			});
		} else {
			this.setState({
				selectedRows: []
			});
		}
	};


	render() {
		const autoHeight = this.state.data.length <= 5;
		const isViewMode = this.props.mode === 'view';

		return (
			<Dialog open={true} maxWidth="lg" fullWidth>
				<DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					Posting Status
					<IconButton onClick={this.handleClose} size="small">
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<EnhancedDataGrid
						nofilter={true}
						rows={this.state.data}
						columns={[
							{
								headerName: '',
								field: 'chk',
								width: 40,
								resizable: false,
								sortable: false,
								renderHeader: () => {
									if (isViewMode) return null;

									return (
										<Checkbox
											indeterminate={
												this.state.selectedRows.length > 0 &&
												!this.isAllSelected()
											}
											checked={this.isAllSelected()}
											onChange={this.handleSelectAll}
										/>
									);
								},
								renderCell: (rowData) => {
									if (isViewMode) return null;

									return (
										<Checkbox
											disabled={rowData.row.posted === 1}
											checked={this.state.selectedRows.some(
												r => r.id === rowData.row.id
											)}
											value={rowData.row.id}
											onClick={this.checkbox_clickHandler}
										/>
									);
								}
							}
,
							{
								headerName: 'Site',
								field: 'mediaSite',
								flex: 2
							},
							{
								headerName: 'MO Invoice Number',
								field: 'intInvoiceNumber',
								flex: 2
							},
							{
								headerName: 'MOS',
								field: 'mosRange',
								flex: 2
							},
							{
								headerName: 'Invoice Amount',
								field: 'invoiceAmount',
								flex: 1,
								valueFormatter: ({ value }) => formatNumber(value)
							},
							{
								headerName: 'Tax Amount',
								field: 'invoiceTaxAmount',
								flex: 1,
								valueFormatter: ({ value }) => formatNumber(value)
							},
							{
								headerName: 'Posting Status',
								field: 'posted',
								flex: 1,
								valueGetter: (params) => (params.row.posted === 1 ? 'Posted' : 'Failed')
							},
							{
								headerName: 'Error Message',
								field: 'message',
								flex: 3,
								renderCell: (params) => {
									const showMessage = params.row.posted === 0;
									const message = showMessage ? params.row.message : '';

									return (
										<Tooltip title={message} placement="top">
											<span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', width: '100%' }}>
												{message}
											</span>
										</Tooltip>
									);
								}
							},
							{
								headerName: 'Last Attempt',
								field: 'postingAttemptDate',
								type: 'date',
								valueGetter: ({ value }) => value || '',
								valueFormatter: ({ value }) => {
									if (!value) return '';
									const d = new Date(value);
									return this.formatter.format(d);
								},
								flex: 2,
							}
						]}
						getRowClassName={(params) => {
							return params.row.posted === 1 ? 'partial-posted-row' : '';
						}}
						getRowId={(row) => row.id}
						disableSelectionOnClick
						options={{
							pageSize: 10,
							maxBodyHeight: autoHeight ? null : 400,
							autoHeight: autoHeight,
							showSelectAllCheckbox: false
						}}
					/>
				</DialogContent>
				<DialogActions>
					{!isViewMode && (
						<Button
							onClick={this.handleRepostAll}
							variant="contained"
							color="primary"
							//disabled={this.state.selectedRows.length === 0}
						>
							Repost
						</Button>
					)}

					{!isViewMode && this.props.canEdit && (
						<Button
							onClick={this.handleEdit}
							variant="contained"
							color="primary"
						>
							Edit
						</Button>
					)}

					{isViewMode && (
						<Button
							color="primary"
							onClick={() => this.props.onView?.()}
						>
							View Invoice
						</Button>
					)}

					<Button
						onClick={this.handleClose}
						variant="contained"
						sx={{
							backgroundColor: '#ededed',
							color: '#616161',          // Material grey text
							border: '1px solid #ededed',
							boxShadow: 'none',         // flat look
							marginRight: '10px',
							'&:hover': {
								backgroundColor: '#d6d6d6', // darker grey
								border: '1px solid #d6d6d6',
								boxShadow: 'none'
							},
							//'&:active': {
							//	boxShadow: 'none'
							//}
						}}
					>
						Close
					</Button>

				</DialogActions>

			</Dialog>
		);
	}
}
